import {
    ICashFlowAdjustmentSettingsTemplate
} from '../../adjustment-pricing-grid/model/ICashFlowAdjustmentSettingsTemplate';

// OFFERING THE DEFAULT TEMPLATE (CR-20260910-001, G3 — Alan's smoke: Maven's template dropdown
// offered only saved templates, so a portfolio with none offered nothing at all).
//
// ⚠ NOBODY STORES DEFAULT TEMPLATES, AND THAT IS THE POINT. The id IS the template:
// `----^{underlying}^{strategy}` encodes everything needed, and the templates service synthesises
// one on demand from the pair. Sweet Price Tracking never lists defaults either — it receives an
// id and synthesises. So this does NOT build a parallel list; the caller asks the service for the
// one default that belongs to this portfolio's pair, and this function decides whether it belongs
// in the dropdown.
//
// Pure, so the two rules below are unit-testable with no DI and no Angular.

/**
 * The saved templates, plus this portfolio's default — when there is one, and when it is not
 * already there.
 *
 * ⚠ RULE 1 — A NULL DEFAULT IS NEVER RENDERED. `getOrCreateDefaultTemplate` returns **null** when
 * the strategy's secure element is not granted. A null row in a dropdown is not an empty row, it
 * is a crash waiting at selection, so an ungranted strategy must produce NO row rather than a
 * blank one.
 *
 * ⚠ RULE 2 — DEDUPE BY ID. If the saved index already carries a `----^…` entry for this pair, the
 * synthesised one is dropped. Two rows sharing an id is the `<No Portfolio>` problem again:
 * entries the trader cannot tell apart which are not in fact the same object. The SAVED one wins,
 * because it is what the trader will have edited.
 *
 * ⚠ RULE 3, and it is the one that is easy to break later — THIS FUNCTION ONLY OFFERS. It returns
 * a LIST. It does not choose, and nothing downstream may fall back to the default when no other
 * template matched: ruling S2 makes the pre-fill visible and changeable and forbids a silent
 * default BY NAME. **Offering is not choosing.** The dangerous line is `templateId = templateId ||
 * defaultTemplate.templateId`, and it must never be written.
 */
export function withDefaultTemplate(
    saved: ICashFlowAdjustmentSettingsTemplate[],
    defaultTemplate: ICashFlowAdjustmentSettingsTemplate | null
): ICashFlowAdjustmentSettingsTemplate[] {

    const list = (saved || []).slice();

    // Rule 1.
    if (!defaultTemplate || !defaultTemplate.templateId) {
        return list;
    }

    // Rule 2.
    for (let i = 0; i < list.length; i++) {
        if (list[i] && list[i].templateId === defaultTemplate.templateId) {
            return list;
        }
    }

    list.push(defaultTemplate);
    return list;
}
